import Phaser from 'phaser';
import BaseScene from './base';
import {
  ControllerButton,
  horizontalMenuItems,
  makeStandardController,
  MenuControls,
  StandardController,
  State,
  StateMachine,
  TextMenu,
  TextMenuItem,
  verticalMenuItems,
} from 'tower/util';
import gameState from 'tower/state/game-state';
import { Flags } from 'tower/types';
import * as C from 'tower/constants';
import { CreditsOptions } from './credits';

export default class TitleScene extends BaseScene {
  controller!: StandardController;
  menuControls!: MenuControls;
  stateMachine!: StateMachine<TitleState>;

  constructor(config: Phaser.Types.Scenes.SettingsConfig = {}) {
    super({
      key: 'title',
      ...config,
    });
  }

  loadResources(scene: Phaser.Scene) {
    scene.load.image('logo', 'img/logo.png');
    scene.load.spritesheet('volume', 'img/volume.png', { frameWidth: 8, frameHeight: 8 });
  }

  create() {
    super.create();

    this.controller = makeStandardController(this);
    this.menuControls = {
      up: this.controller.up,
      down: this.controller.down,
      left: this.controller.left,
      right: this.controller.right,
      confirm: new ControllerButton(this, { buttons: [this.controller.button1, this.controller.start] }),
      cancel: this.controller.button2,
    };

    this.stateMachine = new StateMachine('logo', {
      logo: new LogoState(this),
      newGameConfirm: new NewGameConfirmState(this),
      settings: new SettingsState(this),
    });
  }

  update() {
    this.stateMachine.update();
  }
}

class TitleState extends State {
  constructor(protected scene: TitleScene) {
    super();
  }
}

class LogoState extends TitleState {
  logo!: Phaser.GameObjects.Image;
  attrib!: Phaser.GameObjects.BitmapText;
  menu!: TextMenu;

  init() {
    const { scene } = this;
    this.logo = scene.add.image(128, 60, 'logo').setVisible(false);
    this.attrib = scene.add
      .bitmapText(48, 216, 'textWhite', '   A FAN DEMO BY\n\nLEGITIMATE BUSINESS')
      .setVisible(false);

    this.menu = new TextMenu(
      scene,
      'textWhite',
      gameState.sword === 'whiteSword' ? 'ij ' : 'gh ',
      scene.menuControls,
      {},
    ).setVisible(false);

    this.menu.on('select', async (cursor: string) => {
      switch (cursor) {
        case 'continue':
          this.menu.pauseInput();
          await scene.fadeOut();
          scene.scene.start('level-intro');
          break;
        case 'newgame':
          if (await gameState.hasSave()) {
            return this.transition('newGameConfirm');
          }

          this.menu.pauseInput();
          await scene.fadeOut();
          gameState.resetSave();
          scene.scene.start('game-intro');
          break;
        case 'settings':
          this.transition('settings');
          return;
        case 'credits':
          this.menu.pauseInput();
          await scene.fadeOut();
          const options: CreditsOptions = {
            context: 'title',
          };
          scene.scene.start('credits', options);
          return;
      }
    });
  }

  async makeMenuItems() {
    const hasSave = await gameState.hasSave();

    let yMod = hasSave ? 16 : 0;
    const menuItems = [
      { key: 'newgame', x: 72, y: 128 + yMod, text: 'NEW GAME' },
      { key: 'settings', x: 72, y: 144 + yMod, text: 'SETTINGS' },
      { key: 'credits', x: 72, y: 160 + yMod, text: 'CREDITS' },
    ];
    if (hasSave) {
      menuItems.unshift({ key: 'continue', x: 72, y: 128, text: 'CONTINUE' });
    }

    return verticalMenuItems(menuItems);
  }

  async handleEntered() {
    this.logo.setVisible(true);
    this.attrib.setVisible(true);
    this.menu
      .setMenuItems(await this.makeMenuItems())
      .resumeInput()
      .setVisible(true)
      .resetCursor();
  }

  update() {
    this.menu.update();
  }

  handleExited() {
    this.logo.setVisible(false);
    this.attrib.setVisible(false);
    this.menu.setVisible(false);
  }
}

class NewGameConfirmState extends TitleState {
  box!: Phaser.GameObjects.NineSlice;
  text!: Phaser.GameObjects.BitmapText;
  menu!: TextMenu;

  init() {
    const { scene } = this;

    this.box = scene.add.nineslice(128, 128, 'box', 0, 192, 48, 8, 8, 8, 8).setVisible(false);

    this.text = scene.add.bitmapText(46, 114, 'textWhite', 'DELETE EXISTING SAVE?').setVisible(false);
    const menuItems: TextMenuItem[] = [
      { key: 'confirm', x: 54, y: 134, text: 'YES' },
      { key: 'cancel', x: 134, y: 134, text: 'NO', isCancel: true },
    ];

    this.menu = new TextMenu(
      scene,
      'textWhite',
      gameState.sword === 'whiteSword' ? 'ij ' : 'gh ',
      scene.menuControls,
      horizontalMenuItems(menuItems),
    ).setVisible(false);

    this.menu.on('select', async (cursor: string) => {
      switch (cursor) {
        case 'confirm':
          this.menu.pauseInput();
          await scene.fadeOut();
          gameState.resetSave();
          scene.scene.start('game-intro');
          break;
      }
    });
    this.menu.on('cancel', () => {
      this.transition('logo');
    });
  }

  handleEntered() {
    this.box.setVisible(true);
    this.text.setVisible(true);
    this.menu.resumeInput().setVisible(true).moveCursorTo('cancel');
  }

  update() {
    this.menu.update();
  }

  handleExited() {
    this.box.setVisible(false);
    this.text.setVisible(false);
    this.menu.setVisible(false);
  }
}

class SettingsState extends TitleState {
  box!: Phaser.GameObjects.NineSlice;
  menu!: TextMenu;
  titleBg!: Phaser.GameObjects.Rectangle;
  title!: Phaser.GameObjects.BitmapText;
  volumeImages: Phaser.GameObjects.Image[] = [];
  music!: Phaser.Sound.BaseSound;

  init() {
    const { scene } = this;

    this.music = scene.sound.add('dungeon', { loop: true });

    this.box = scene.add.nineslice(128, 128, 'box', 0, 176, 104, 8, 8, 8, 8).setVisible(false);
    this.titleBg = scene.add.rectangle(128, 80, 80, 8, 0x000000).setVisible(false);
    this.title = scene.add.bitmapText(96, 76, 'textWhite', 'SETTINGS').setVisible(false);

    for (let k = 0; k < 8; k++) {
      this.volumeImages.push(scene.add.image(140 + k * 8, 104, 'volume', k).setVisible(false));
    }

    const menuItems: TextMenuItem[] = [
      { key: 'volume', x: 56, y: 100, text: 'VOLUME' },
      {
        key: 'toggleFlash',
        x: 56,
        y: 124,
        text: this.disableFlashingText(gameState.getFlag('disableFlashing')),
      },
      { key: 'cancel', x: 56, y: 156, text: 'END', isCancel: true },
    ];

    this.menu = new TextMenu(
      scene,
      'textWhite',
      gameState.sword === 'whiteSword' ? 'ij ' : 'gh ',
      scene.menuControls,
      verticalMenuItems(menuItems),
    ).setVisible(false);

    this.menu.on('focus', (cursor: string) => {
      if (cursor === 'volume') {
        this.music.play();
      } else {
        this.music.stop();
      }
    });

    this.menu.on('select', async (cursor: string) => {
      switch (cursor) {
        case 'toggleFlash':
          gameState.setFlag('disableFlashing', !gameState.getFlag('disableFlashing'));
          return;
      }
    });
    this.menu.on('cancel', () => {
      this.transition('logo');
    });
  }

  setVolumeDisplay(volume: number) {
    for (let k = 0; k < 8; k++) {
      const image = this.volumeImages[k];
      const level = C.VOLUME_LEVELS[k + 1];
      image.setFrame(volume >= level ? k + 8 : k);
    }
  }

  handleEntered() {
    this.box.setVisible(true);
    this.titleBg.setVisible(true);
    this.title.setVisible(true);
    this.menu.resumeInput().setVisible(true).resetCursor();
    gameState.on('flagchange', this.handleFlagChange);
    gameState.on('volumechanged', this.handleVolumeChanged);
    this.setVolumeDisplay(gameState.volume);
    for (const image of this.volumeImages) {
      image.setVisible(true);
    }
  }

  disableFlashingText(flagValue: boolean) {
    return `${flagValue ? 'l' : 'k'} DISABLE\n     FLASHING`;
  }

  handleFlagChange = (flags: Flags) => {
    this.menu.setMenuItemText('toggleFlash', this.disableFlashingText(flags.disableFlashing));
  };

  handleVolumeChanged = (volume: number) => {
    this.setVolumeDisplay(volume);
  };

  update() {
    const { menu, scene } = this;
    menu.update();

    if (menu.currentItem()?.key === 'volume') {
      if (scene.controller.right.justDown) {
        const nextVolume = C.VOLUME_LEVELS.find((v) => v > gameState.volume);
        if (nextVolume !== undefined) {
          gameState.volume = nextVolume;
        }
      } else if (scene.controller.left.justDown) {
        const nextVolume = C.VOLUME_LEVELS.toReversed().find((v) => v < gameState.volume);
        if (nextVolume !== undefined) {
          gameState.volume = nextVolume;
        }
      }
    }
  }

  handleExited() {
    this.box.setVisible(false);
    this.menu.setVisible(false);
    this.titleBg.setVisible(false);
    this.title.setVisible(false);
    gameState.off('flagchange', this.handleFlagChange);
    gameState.off('volumechanged', this.handleVolumeChanged);
    for (const image of this.volumeImages) {
      image.setVisible(false);
    }
    this.music.stop();
  }
}
